/**
 * F31 (Wave 2B) — admin sport-skill-descriptors CMS page.
 *
 * Route: `/sport-skill-descriptors`
 * Permission: `sport_skill_descriptors:read` (page) + `sport_skill_descriptors:write`
 * (Add / Edit / Delete actions — hidden when the actor lacks write).
 *
 * Descriptors are grouped by sport. Within each sport group, rows are sorted
 * by level band (ascending). Add / Edit open the DescriptorEditDialog;
 * Delete opens a confirm Dialog. Mirrors the admin CMS table conventions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { SportSkillDescriptor, UpsertSportSkillDescriptorInput } from '@bravemove/types';
import { adminSportSkillDescriptorsApi } from '../../api/sportSkillDescriptors';
import { PageHeader } from '../../components/PageHeader';
import { DescriptorEditDialog } from '../../components/skill/DescriptorEditDialog';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';

export function AdminSportSkillDescriptorsPage() {
  const { t } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const canWrite = auth.adminUser?.permissions.includes('sport_skill_descriptors:write') ?? false;

  const [rows, setRows] = useState<SportSkillDescriptor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<SportSkillDescriptor | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SportSkillDescriptor | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await adminSportSkillDescriptorsApi.list());
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const openAdd = () => {
    setEditTarget(null);
    setEditError(null);
    setEditOpen(true);
  };
  const openEdit = (d: SportSkillDescriptor) => {
    setEditTarget(d);
    setEditError(null);
    setEditOpen(true);
  };

  const handleSave = async (input: UpsertSportSkillDescriptorInput): Promise<void> => {
    setEditError(null);
    try {
      await adminSportSkillDescriptorsApi.upsert(input);
      setEditOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:skill.descriptors.edit_cta', { defaultValue: 'Saved' }),
      });
      await refresh();
    } catch (err) {
      setEditError(translateApiError(err, t));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await adminSportSkillDescriptorsApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:skill.descriptors.delete_cta', { defaultValue: 'Deleted' }),
      });
      await refresh();
    } catch (err) {
      // TGS-12 — surface the failure inside the (still-open) confirm Dialog,
      // mirroring handleSave; previously the rejection escaped `void
      // handleDelete()` as an unhandled rejection with zero admin feedback.
      setDeleteError(translateApiError(err, t));
    } finally {
      setDeleting(false);
    }
  };

  // Group rows by sport name, sorted by band min within each group.
  const groupedBySport = useMemo<
    ReadonlyArray<{ sport: string; items: SportSkillDescriptor[] }>
  >(() => {
    const map = new Map<string, SportSkillDescriptor[]>();
    for (const row of rows) {
      const group = map.get(row.sportName);
      if (group) {
        group.push(row);
      } else {
        map.set(row.sportName, [row]);
      }
    }
    const sorted = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    return sorted.map(([sport, items]) => ({
      sport,
      items: [...items].sort((a, b) => a.levelBandMin - b.levelBandMin),
    }));
  }, [rows]);

  const sportColumns = useMemo<readonly TableColumn<SportSkillDescriptor>[]>(
    () => [
      {
        key: 'band',
        header: t('admin:skill.descriptors.column.band', { defaultValue: 'Level band' }),
        cell: (row) => (
          <span className="font-mono text-[12.5px] text-ink-2 tabular-nums">
            {row.levelBandMin.toFixed(1)}–{row.levelBandMax.toFixed(1)}
          </span>
        ),
      },
      {
        key: 'body',
        header: t('admin:skill.descriptors.column.body', { defaultValue: 'Description' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 line-clamp-2 max-w-[40ch]">{row.bodyText}</span>
        ),
      },
      ...(canWrite
        ? [
            {
              key: 'actions',
              header: t('admin:skill.descriptors.column.actions', { defaultValue: 'Actions' }),
              align: 'end' as const,
              cell: (row: SportSkillDescriptor) => (
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(row)}
                    data-testid={`descriptor-edit-${row.id}`}
                  >
                    {t('admin:skill.descriptors.edit_cta', { defaultValue: 'Edit' })}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteTarget(row)}
                    data-testid={`descriptor-delete-${row.id}`}
                  >
                    {t('admin:skill.descriptors.delete_cta', { defaultValue: 'Delete' })}
                  </Button>
                </div>
              ),
            },
          ]
        : []),
    ],
    [t, canWrite],
  );

  return (
    <div className="space-y-6" data-testid="sport-skill-descriptors-page">
      <PageHeader
        title={t('admin:skill.descriptors.page_title', { defaultValue: 'Sport level descriptors' })}
        subtitle={t('admin:skill.descriptors.page_subtitle', {
          defaultValue: 'Author the per-level descriptions players see when self-rating.',
        })}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={openAdd} data-testid="descriptor-add">
              {t('admin:skill.descriptors.add_cta', { defaultValue: 'Add descriptor' })}
            </Button>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="sport-skill-descriptors-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:skill.descriptors.empty', {
            defaultValue: 'No descriptors yet — add one to get started.',
          })}
        />
      ) : (
        /* Grouped by sport — each sport gets its own Card + Table */
        <div className="space-y-4">
          {groupedBySport.map(({ sport, items }) => (
            <Card key={sport}>
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-[13px] font-semibold text-ink capitalize">
                  {sport.replace(/-/g, ' ')}
                </h2>
                <span className="font-mono text-[11px] text-ink-3 bg-surface-2 rounded px-1.5 py-0.5">
                  {items.length}
                </span>
              </div>
              <Table
                data={items}
                columns={sportColumns as TableColumn<SportSkillDescriptor>[]}
                rowKey={(row) => row.id}
              />
            </Card>
          ))}
        </div>
      )}

      <DescriptorEditDialog
        open={editOpen}
        descriptor={editTarget}
        onSave={handleSave}
        onClose={() => setEditOpen(false)}
        errorMessage={editError}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={() => void handleDelete()}
        destructive
        loading={deleting}
        title={t('admin:skill.descriptors.delete_confirm_title', {
          defaultValue: 'Delete this descriptor?',
        })}
        description={t('admin:skill.descriptors.delete_confirm_body', {
          defaultValue:
            "Players will see the generic placeholder for this level until it's re-added.",
        })}
        confirmLabel={t('admin:skill.descriptors.delete_cta', { defaultValue: 'Delete' })}
      >
        {deleteError ? (
          <div data-testid="descriptor-delete-error">
            <Banner tone="danger">{deleteError}</Banner>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

export default AdminSportSkillDescriptorsPage;
